import { Stack, Textarea } from "@chakra-ui/react";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { MinimumRequirementResponseType } from "../../../../autogen/bff-api";
import { verdictToStatusVariant } from "../../../../common/components/StatusVariant";
import { ReadonlyAnswerCard, ReadonlyFieldLabel } from "../../../sourcing-events/bid-readonly/ReadonlyAnswerCard";
import { QualificationVerdictControl } from "./QualificationVerdictControl";

interface Props {
  id: string;
  title: string;
  description?: string | null;
  responseType: MinimumRequirementResponseType;
  supplierAnswer: boolean | undefined;
  supplierComment?: string | null;
  verdict: boolean | null;
  comment: string;
  onVerdictChange: (id: string, value: boolean | null) => void;
  onCommentChange: (id: string, value: string) => void;
  isReadOnly?: boolean;
}

// Memoized so editing one card does not re-render (and re-render the read-only TipTap description
// of) every other card. The parent passes stable per-id handlers, so only props of the edited row
// change.
export const MinimumRequirementReviewCard = memo(function MinimumRequirementReviewCard({
  id,
  title,
  description,
  responseType,
  supplierAnswer,
  supplierComment,
  verdict,
  comment,
  onVerdictChange,
  onCommentChange,
  isReadOnly,
}: Props) {
  const { t } = useTranslation();
  const followsSupplierAnswer = responseType === MinimumRequirementResponseType.YesNo;
  const effectiveVerdict = followsSupplierAnswer ? supplierAnswer ?? null : verdict;
  // A requirement the supplier never answered (e.g. added after this bid was submitted) cannot be
  // reviewed — the backend rejects a verdict for it — so lock the controls instead of letting the
  // buyer make an edit that would be discarded.
  const reviewable = supplierAnswer !== undefined;
  const locked = followsSupplierAnswer || !reviewable;

  return (
    <ReadonlyAnswerCard
      accent={verdictToStatusVariant(effectiveVerdict)}
      title={title}
      descriptionHtml={description}
      answer={
        supplierAnswer === undefined
          ? { kind: "empty" }
          : { kind: "boolean", value: supplierAnswer, comment: supplierComment }
      }
      headerAction={
        locked ? (
          <QualificationVerdictControl value={effectiveVerdict} isLocked />
        ) : (
          <QualificationVerdictControl value={verdict} onChange={(v) => onVerdictChange(id, v)} isReadOnly={isReadOnly} />
        )
      }
      footer={
        <Stack spacing="2">
          <ReadonlyFieldLabel>{t("Evaluation comment")}</ReadonlyFieldLabel>
          <Textarea
            value={comment}
            onChange={(e) => onCommentChange(id, e.target.value)}
            placeholder={String(t("Write comment..."))}
            rows={3}
            isReadOnly={isReadOnly || !reviewable}
            resize="vertical"
            bg="smBackground"
          />
        </Stack>
      }
    />
  );
});
